import { Injectable, inject } from '@angular/core';
import { Observable, switchMap, take } from 'rxjs';
import { StudentAssignmentStateService } from './student-assignment-state.service';
import { StudentSubmissionService } from './student-submission.service';
import { StudentAnswerService } from './student-answer.service';
import { StudentGradingService } from './student-grading.service';
import {
  AssignmentPreview,
  GetSubmissionResponse,
  SubmissionResponse,
  SubmissionResult,
  SubmissionQuestion,
  SaveAnswerDto,
  StudentAssignmentStep,
} from './student-assignment.models';

/**
 * Facade service that provides a unified API for the student assignment flow.
 * Delegates to focused sub-services for state, submissions, answers, and grading.
 */
@Injectable({
  providedIn: 'root',
})
export class StudentAssignmentService {
  private readonly state = inject(StudentAssignmentStateService);
  private readonly submissionService = inject(StudentSubmissionService);
  private readonly answerService = inject(StudentAnswerService);
  private readonly gradingService = inject(StudentGradingService);

  // ============================================
  // STATE (delegated to StudentAssignmentStateService)
  // ============================================

  readonly shareCode$ = this.state.shareCode$;
  readonly currentStep$ = this.state.currentStep$;
  readonly preview$ = this.state.preview$;
  readonly submission$ = this.state.submission$;
  readonly questions$ = this.state.questions$;
  readonly currentQuestionIndex$ = this.state.currentQuestionIndex$;
  readonly currentQuestion$ = this.state.currentQuestion$;
  readonly localAnswers$ = this.state.localAnswers$;
  readonly isLoading$ = this.state.isLoading$;
  readonly isSubmitting$ = this.state.isSubmitting$;
  readonly isGrading$ = this.state.isGrading$;
  readonly gradingProgressStep$ = this.state.gradingProgressStep$;
  readonly gradingProgressLabel$ = this.state.gradingProgressLabel$;
  readonly result$ = this.state.result$;
  readonly error$ = this.state.error$;
  readonly errorType$ = this.state.errorType$;
  readonly expiresAt$ = this.state.expiresAt$;

  // ============================================
  // GETTERS (delegated to StudentAssignmentStateService)
  // ============================================

  get shareCode(): string | null {
    return this.state.shareCode;
  }

  get currentStep(): StudentAssignmentStep {
    return this.state.currentStep;
  }

  get currentQuestionIndex(): number {
    return this.state.currentQuestionIndex;
  }

  get questions(): SubmissionQuestion[] {
    return this.state.questions;
  }

  get totalQuestions(): number {
    return this.state.totalQuestions;
  }

  get isLastQuestion(): boolean {
    return this.state.isLastQuestion;
  }

  // ============================================
  // STEP MANAGEMENT (delegated to state)
  // ============================================

  setStep(step: StudentAssignmentStep): void {
    this.state.setStep(step);
  }

  // ============================================
  // PREVIEW & SUBMISSION (delegated to StudentSubmissionService)
  // ============================================

  loadPreview(shareCode: string): Observable<AssignmentPreview> {
    return this.submissionService.loadPreview(shareCode);
  }

  startSubmission(): Observable<SubmissionResponse> {
    return this.submissionService.startSubmission();
  }

  loadSubmission(): Observable<GetSubmissionResponse> {
    return this.submissionService.loadSubmission();
  }

  // ============================================
  // NAVIGATION & ANSWERS (delegated to StudentAnswerService)
  // ============================================

  navigateToQuestion(index: number): void {
    this.answerService.navigateToQuestion(index);
  }

  nextQuestion(): void {
    this.answerService.nextQuestion();
  }

  prevQuestion(): void {
    this.answerService.prevQuestion();
  }

  saveAnswer(questionId: string, answer: SaveAnswerDto): void {
    this.answerService.saveAnswer(questionId, answer);
  }

  getLocalAnswer(questionId: string): SaveAnswerDto | undefined {
    return this.answerService.getLocalAnswer(questionId);
  }

  // ============================================
  // GRADING (delegated to StudentGradingService)
  // ============================================

  submitAndGrade(): Observable<SubmissionResult> {
    // Flush pending answer before submitting to ensure backend has the latest data
    return this.answerService.flushPendingAnswer().pipe(
      switchMap(() => this.gradingService.submitAndGrade()),
    );
  }

  loadResult(): Observable<SubmissionResult> {
    return this.gradingService.loadResult();
  }

  handleTimeExpired(): void {
    // Flush pending answer before auto-submitting on time expiry
    this.answerService
      .flushPendingAnswer()
      .pipe(take(1))
      .subscribe({
        next: () => this.gradingService.handleTimeExpired(),
        error: () => this.gradingService.handleTimeExpired(),
      });
  }

  // ============================================
  // RESET (delegated to state)
  // ============================================

  reset(): void {
    this.state.reset();
  }

  /**
   * Partial reset for teacher retest — clears submission/result/answers
   * while preserving preview and share code.
   */
  resetForRetest(): void {
    this.state.resetForRetest();
  }
}
